import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Inject,
  Optional,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { DocumentModel } from '@data-layer/models/documents';
import {
  CountryModel,
  CurrencyRateModel,
  HtsNumberModel,
  ShipmentModel,
  StateModel,
  SimilarHtsSuggestionModel,
  UnitModel,
  ValidationMessageModel,
} from '@data-layer/models/common';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { Location } from '@angular/common';
import { WidgetsService } from '@services/widgets.service';
import { BinaRepository, CodeRepository, CurrenciesRepository } from '@data-layer/repositories';
import { catchError, debounceTime, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  CANADA_COUNTRY_ID,
  LINE_ITEM_DESCRIPTION_MAX_LENGTH,
  MEXICO_COUNTRY_ID,
  USA_COUNTRY_ID,
  WEIGHT_UNIT_KG,
} from '@constants/constants';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { enumEntrySummarySubmissionType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { IEsDocumentStateUpdate } from '@shared/es-mutual/interfaces';
import { CUSTOM_MASK, HTS_MASK } from '@helpers/custom-validator';
import { DateHelper } from '@helpers/date.helper';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { IEsValidationData } from '@interfaces/es-validation-data';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { BinaSidebarComponent } from '@shared/bina-mutual/components';
import { AppService } from '@app/app.service';
import { BinaCatalogSearchSidebarComponent } from '@shared/bina-shared/components';
import { FavoriteHtsNumberModel } from '@app/data-layer/models/common/favorite-hts-number.model';
import { UtilsHelper } from '@helpers/utils-helper';
import { CompanyModel } from '@app/data-layer/models/companies';
import { enumHtsChatScope } from '@data-layer/enums/common';
import { HtsAssistantDrawerComponent } from '@shared/hts-assistant/components';
import { enumHtsAssistantMode } from '@shared/hts-assistant/enums';
import { HtsAssistantDescriptionGuard } from '@shared/hts-assistant/helpers/hts-assistant-description.guard';
import { HtsAssistantHelper } from '@shared/hts-assistant/helpers/hts-assistant.helper';
import { IHtsAssistantHtsFound } from '@shared/hts-assistant/interfaces';
import { HTS_CHAT_SCOPE, HtsAssistantLauncherService, HtsLineInsertService } from '@shared/hts-assistant/services';
import { HTS_SUGGESTIONS_DEBOUNCE_MS } from '@shared/hts-assistant/const/hts-assistant.const';

interface ILeadHtsUnits {
  leadUnit1?: string;
  leadUnit2?: string;
  leadUnit3?: string;
}

@Component({
  selector: 'es-line-item-default-desktop',
  template: `
    <p class="sidebar-form-description" *ngIf="isShowHint">{{ 'ENTRY_SUMMARY.INFORMATION_LINE' | translate }}</p>

    <form [formGroup]="itemForm" autocomplete="off" (ngSubmit)="submit()">
      <div class="form-row">
        <div class="col-12">
          <app-control-wrapper
            [control]="itemForm.get('description')"
            [classGroup]="descriptionGroupClass"
            classHeader="d-flex justify-content-between"
            [errors]="errors"
          >
            <label for="description" control-label>{{ 'LABELS.ITEM_DESCRIPTION' | translate }}</label>
            <button
              *ngIf="isShowFindInCatalog"
              control-action-button
              type="button"
              class="btn btn-link shadow-none"
              (click)="findInCatalog()"
            >
              {{ 'LABELS.FIND_IN_CATALOG' | translate }}
            </button>
            <textarea
              control-wrapper
              id="description"
              class="form-control"
              formControlName="description"
              [dropSpecialCharacters]="false"
              [patterns]="CUSTOM_MASK"
              [mask]="'F{60}'"
              [appMaskErrorTooltip]="'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL'"
              [maxMaskLength]="60"
              [rows]="2"
              (input)="onFavoriteHtsItemsSearch()"
            ></textarea>
            <div custom-error class="invalid-form-control" *ngIf="isShowDescriptionMissingReadonly">
              {{ 'HTS_ASSISTANT.DESCRIPTION_REQUIRED_HINT' | translate }}
            </div>
          </app-control-wrapper>

          <ng-container *ngIf="isShowFastSearch">
            <app-bina-catalog-fast-search-list
              [resultItems]="favoriteHtsNumberSearchedItems"
              [suggestions]="favoriteHtsSuggestions"
              [isShowCatalogLink]="isShowFindInCatalog"
              (htsInsert)="onCatalogPick($event)"
              (suggestionInsert)="onSuggestionPick($event)"
              (findInCatalog)="findInCatalog()"
              (clear)="clearSearchedFavoriteHtsItems()"
            ></app-bina-catalog-fast-search-list>
          </ng-container>
        </div>
      </div>

      <div class="form-row">
        <div [ngClass]="isHideFindHts ? 'col-12' : 'col-9'">
          <app-entry-hts-control
            [tooltipData]="'TOOLTIP.HTS_CODE_TOOLTIP' | translate"
            [fc]="itemForm.get('htsNumber')"
            [label]="'FORM_LABEL.HTS_CODE'"
            [isRequired]="isRequiredHtsNumber"
            [isDisableValidation]="isCustomBroker"
            (getHtsUnit)="getHtsUnits($event)"
            [helper]="'FORM_HELPER.ITEM_HTS_CODE'"
            [placeholder]="'NNNN.NN.NNNN'"
            [matchLength]="[10]"
            [arrivalDate]="arrivalDate"
            [shouldNotMatchHTSConfig]="shouldNotMatchHTSConfig.htsNumber"
          ></app-entry-hts-control>
        </div>

        <div class="col-3 text-right" *ngIf="!isHideFindHts">
          <div class="find-hts">
            <button type="button" class="btn btn-outline-bordered" (click)="findHTS()">{{ 'FIND_HTS' | translate }}</button>
          </div>
        </div>
      </div>

      <div class="form-row">
        <div class="col-12">
          <auto-select
            [errors]="errors"
            [fc]="itemForm.get('countryOfOriginId')"
            [id]="'countryOfOrigin'"
            [bindValue]="'id'"
            [bindLabel]="['code', 'description']"
            [clearable]="true"
            [initializeItems]="initCountryOfOrigin"
            [label]="'FORM_LABEL.COUNTRY_OF_ORIGIN'"
            [helper]="'FORM_HELPER.COUNTRY_OF_ORIGIN'"
            [change]="getCountries()"
          ></auto-select>
        </div>
      </div>

      <div *ngIf="showState" class="form-row">
        <div class="col-12">
          <auto-select
            [errors]="errors"
            [isShowHelper]="true"
            [fc]="itemForm.get('stateOfOrigin')"
            [id]="'stateOfOrigin'"
            [bindValue]="'code'"
            [bindLabel]="['code', 'description']"
            [clearable]="true"
            [initializeItems]="initStateOfOrigin"
            [label]="'FORM_LABEL.STATE_OF_ORIGIN'"
            [change]="getStates()"
          ></auto-select>
        </div>
      </div>

      <div class="form-row">
        <div class="col-6">
          <div class="form-row">
            <div class="col-12">
              <label for="grossWeight">{{ 'LABELS.GROSS_WEIGHT' | translate }}</label>
            </div>

            <div class="col-12">
              <app-control-wrapper [control]="itemForm.get('grossWeight')" [errors]="errors" [isShowHelper]="false">
                <div class="d-flex" control-wrapper>
                  <input
                    control-wrapper
                    id="grossWeight"
                    class="form-control"
                    formControlName="grossWeight"
                    type="text"
                    inputmode="numeric"
                    [pattern]="decimalInputMap.pattern"
                    [mask]="decimalInputMap.mask"
                    [dropSpecialCharacters]="false"
                    [appMaskErrorTooltip]="decimalInputMap.maskErrorTooltip"
                  />
                  <div class="form-control explanation">{{ WEIGHT_UNIT_KG }}</div>
                </div>
              </app-control-wrapper>
            </div>
          </div>
        </div>
        <div
          [ngClass]="{ 'col-12': invoiceCurrencyCode !== DEFAULT_CURRENCY_CODE, 'col-6': invoiceCurrencyCode === DEFAULT_CURRENCY_CODE }"
        >
          <div class="form-row">
            <div class="col-12">
              <label for="value">
                {{ 'FORM_LABEL.ENTERED_VALUE' | translate }}
                <span class="ml-1" *ngIf="invoiceCurrencyCode !== DEFAULT_CURRENCY_CODE">
                  ({{ 'FORM_LABEL.TODAY_EXCHANGE_RATE' | translate }})
                </span>
              </label>
            </div>

            <div
              [ngClass]="{
                'col-6': invoiceCurrencyCode !== DEFAULT_CURRENCY_CODE,
                'col-12': invoiceCurrencyCode === DEFAULT_CURRENCY_CODE
              }"
            >
              <app-control-wrapper [control]="itemForm.get('value')" [errors]="errors">
                <div class="d-flex" control-wrapper>
                  <input
                    control-wrapper
                    id="value"
                    class="form-control currency"
                    formControlName="value"
                    type="text"
                    inputmode="numeric"
                    [pattern]="currencyInputMap.pattern"
                    [mask]="currencyInputMap.mask"
                    [thousandSeparator]="currencyInputMap.thousandSeparator"
                    [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                  />
                  <div class="form-control explanation">{{ invoiceCurrencyCode }}</div>
                </div>
              </app-control-wrapper>
            </div>

            <div class="col-6" *ngIf="invoiceCurrencyCode !== DEFAULT_CURRENCY_CODE">
              <div class="form-group">
                <div class="d-flex">
                  <input
                    id="second"
                    disabled
                    class="form-control currency"
                    [value]="itemForm.get('value').value * invoiceCurrencyRate | currency : DEFAULT_CURRENCY_CODE : '' : '1.2-2'"
                  />
                  <div class="form-control explanation">{{ DEFAULT_CURRENCY_CODE }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div formArrayName="units" *ngFor="let item of formUnits.controls; let i = index; let last = last">
        <div [formGroupName]="i">
          <div class="form-row">
            <div [ngClass]="{ 'col-4 col-md-5': isCustomBroker, 'col-5': !isCustomBroker }">
              <app-control-wrapper [control]="item.get('quantity' + (i + 1))" [errors]="errors" [isShowHelper]="false">
                <label for="quantity{{ i + 1 }}" control-label>{{ 'LABELS.QUANTITY' | translate }} {{ i + 1 }}</label>
                <input
                  control-wrapper
                  id="quantity{{ i + 1 }}"
                  class="form-control"
                  formControlName="quantity{{ i + 1 }}"
                  type="text"
                  inputmode="numeric"
                  [pattern]="decimalInputMap.pattern"
                  [mask]="decimalInputMap.mask"
                  [dropSpecialCharacters]="false"
                  [appMaskErrorTooltip]="decimalInputMap.maskErrorTooltip"
                />
              </app-control-wrapper>
            </div>
            <div *ngIf="!isCustomBroker" class="col-7 col-md-5">
              <div class="form-group">
                <label>{{ 'FORM_LABEL.UNIT_OF_MEASURE' | translate }} {{ i + 1 }}</label>
                <div class="form-control box">{{ item.get('unitOfMeasure' + (i + 1)).value }}</div>
              </div>
            </div>
            <div *ngIf="isCustomBroker" class="col-8 col-md-7">
              <button type="button" class="btn btn-link btn-unit-delete" (click)="removeUnit(i)">
                {{ 'BUTTON.DELETE' | translate }}
              </button>
              <auto-select
                id="unitOfMeasure{{ i + 1 }}"
                [label]="('FORM_LABEL.UNIT_OF_MEASURE' | translate) + ' ' + (i + 1)"
                [requiredLabel]="false"
                [bindLabel]="['code']"
                [bindOption]="['code']"
                [bindValue]="'code'"
                [change]="searchUnits()"
                [fc]="item.get('unitOfMeasure' + (i + 1))"
                [errors]="errors"
                [isShowHelper]="false"
              ></auto-select>
            </div>
          </div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <div class="custom-control custom-checkbox">
            <input type="checkbox" formControlName="hasNaftaNetCostIndicator" class="custom-control-input" id="hasNaftaNetCostIndicator" />
            <label class="custom-control-label" for="hasNaftaNetCostIndicator">
              {{ 'FORM_LABEL.HAVE_TRADE_CERTIFICATE' | translate }}
            </label>
          </div>
        </div>
      </div>

      <div class="form-row mt-2" *ngIf="isShowClearForm">
        <div class="col-12">
          <button type="button" class="btn btn-link btn-remove" (click)="clearForm()">
            <i class="fa fa-trash"></i>
            {{ 'FORM_LABEL.CLEAR_FORM' | translate }}
          </button>
        </div>
      </div>

      <div class="sidebar-form-buttons" *ngIf="!isHideFooter">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ENTRY_SUMMARY'" (decline)="submit(false)"></missing-fields>
  `,
  styleUrls: ['./es-line-item-form-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsLineItemFormDesktopComponent implements OnInit, OnDestroy {
  @Input() public itemId: number;
  @Input() public shipmentId: number;
  @Input() public invoice: InvoiceModel;
  @Input() public isShowHint: boolean;
  @Input() public isHideFooter: boolean;
  @Input() public validateImmediately: boolean;
  // Additive: when true, the form is editable regardless of EntrySummaryActionsHelper.isCanEdit
  // (which excludes BROKER_REVIEW_PENDING). Used by the broker review page; default false →
  // user-side behaviour is unchanged.
  @Input() public forceEditable: boolean = false;
  /**
   * #13384 F7: embedded hosts that already live in an as-window (the assistant's line panel). "Find HTS" is hidden and
   * "Find in catalog" is delegated to the host (`findInCatalogRequested`) — both would open another as-window and
   * replace the host otherwise.
   */
  @Input() public isHideFindHts: boolean = false;
  @Output() public lineDataSaved: EventEmitter<LineModel> = new EventEmitter();
  /** #13383 F6: "Find HTS" persisted a NEW line; hosts that own the item id (the invoice wizard) must adopt it. */
  @Output() public lineCreated: EventEmitter<number> = new EventEmitter();
  /** The backend wrote a catalog / similar-product code onto the saved line (#13411 / #13517, tagged companies). */
  @Output() public lineInserted: EventEmitter<LineModel> = new EventEmitter();
  /** The pre-save or the backend insert was refused (the alert is shown): hosts re-read the form's dirty state. */
  @Output() public lineInsertFailed: EventEmitter<void> = new EventEmitter();
  /** `isHideFindHts` hosts open the Bina catalog themselves and feed the pick back through `onHtsInserted`. */
  @Output() public findInCatalogRequested: EventEmitter<void> = new EventEmitter();

  public company: CompanyModel;
  public itemForm: UntypedFormGroup;
  public arrivalDate: string;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public UNITS_LINES_LENGTH: number = 3;
  public decimalInputMap: InputTypeMap = INPUT_TYPES_MAP.DECIMAL;
  public integerInputMap: InputTypeMap = INPUT_TYPES_MAP.INTEGER;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public invoiceCurrencyCode: string;
  public invoiceCurrencyRate: number;
  public CUSTOM_MASK: any = CUSTOM_MASK;
  public addSpecialChapterHTSCode: boolean = false;
  /** `assistant` comes first: "Find HTS" without a description sets only that error, so its hint wins over the generic one. */
  public errors: { assistant: string; required: string; maxlength: string } = {
    assistant: 'HTS_ASSISTANT.DESCRIPTION_REQUIRED_HINT',
    required: 'FORM_ERROR.REQUIRED',
    maxlength: 'FORM_ERROR.MAX_LENGTH',
  };
  public HTS_MASK: string = HTS_MASK;
  public isCanEdit: boolean = false;
  /**
   * Embedded hosts (the assistant's line panel, #13384 F15) show the saved item read-only until the user asks to edit.
   * An input setter (not a method) so the switch happens inside the host's change detection, never after this view was checked.
   */
  @Input()
  public set isReadMode(isReadMode: boolean) {
    this.isReadModeValue = isReadMode;
    this.applyReadMode();
  }
  public get isReadMode(): boolean {
    return this.isReadModeValue;
  }
  private isReadModeValue: boolean = false;
  /** "Find HTS" / the panel asked for a description once: a still-empty field stays flagged even in read mode. */
  private isAssistantDescriptionRequested: boolean = false;
  public isCanRemoveDocuments: boolean = false;
  /** Similar products the company classified before, for the typed description (#13517, tagged companies). */
  public favoriteHtsSuggestions: SimilarHtsSuggestionModel[] = [];
  public currentItem: LineModel;
  public unitsToMachForSpecialHts: string[] = [];
  public entrySummary: EntrySummaryModel;
  public attachedDocuments: DocumentModel[] = [];
  public isShowDocuments: boolean = false;
  public isCustomBroker: boolean = false;
  public validationMessages: ValidationMessageModel[] = [];
  public WEIGHT_UNIT_KG: string = WEIGHT_UNIT_KG;
  public isInit: boolean = false;
  public initCountryOfExport: CountryModel[] = [];
  public initStateOfOrigin: StateModel[] = [];
  public initCountryOfOrigin: CountryModel[] = [];
  public shouldNotMatchHTSConfig = this.createShouldNotMatchHTSConfig();
  public uploadDocumentsDescsription: string;
  public uploadDocumentsDescsriptionMobile: string;
  public lastSearchedCountries: CountryModel[] = [];
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public favoriteHtsNumberSearchedItems: FavoriteHtsNumberModel[] = [];
  public companyFavoriteHtsNumbers: FavoriteHtsNumberModel[] = [];

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  private currentShipment: ShipmentModel;
  private currentInvoice: InvoiceModel;
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();
  private isPreSavingForAssistant: boolean = false;
  private isDestroyed: boolean = false;
  /** `lastModified` of the store line when the drawer opened: the form is re-read only if the line changed meanwhile. */
  private lineModifiedAtDrawerOpen: string | null = null;
  private favoriteHtsItemsSearchSubject = new Subject<string>();

  private leadHtsUnits: ILeadHtsUnits = {};
  private previousCountryOfOriginId: number | null = null;

  constructor(
    private router: Router,
    private entrySummaryStateService: EntrySummaryStateService,
    private activeRoute: ActivatedRoute,
    private fh: FormHelper,
    private location: Location,
    private binaRepository: BinaRepository,
    private widgetsService: WidgetsService,
    private currenciesRepository: CurrenciesRepository,
    private codeRepository: CodeRepository,
    private companiesService: CompaniesService,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private htsAssistantLauncherService: HtsAssistantLauncherService,
    private htsLineInsertService: HtsLineInsertService,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(HTS_CHAT_SCOPE) private htsChatScope: enumHtsChatScope,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();

    this.itemForm = this.fh.createLineItemForm();
    if (this.validateImmediately) {
      this.fh.validateAllFields(this.itemForm);
    }

    this.itemForm
      .get('countryOfOriginId')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((countryId: number) => {
        if (!this.addSpecialChapterHTSCode) {
          this.toggleSpecialHts();
        }

        const selectedCountry: CountryModel =
          this.lastSearchedCountries.find((c: CountryModel) => c.id === countryId) ||
          this.initCountryOfOrigin.find((c: CountryModel) => c.id === countryId);
        if (selectedCountry) {
          this.itemForm.get('countryOfOrigin').setValue(selectedCountry, { emitEvent: false });
        }

        const countryChanged: boolean = this.previousCountryOfOriginId !== null && this.previousCountryOfOriginId !== countryId;
        const requiresState: boolean = countryId === CANADA_COUNTRY_ID || countryId === MEXICO_COUNTRY_ID || countryId === USA_COUNTRY_ID;

        if (countryChanged) {
          this.itemForm.get('stateOfOrigin').reset();
        }
        if (requiresState) {
          this.itemForm.get('stateOfOrigin').setValidators(Validators.required);
        } else {
          this.itemForm.get('stateOfOrigin').clearValidators();
        }
        this.itemForm.get('stateOfOrigin').updateValueAndValidity({ emitEvent: false });

        this.previousCountryOfOriginId = countryId;
        this.cdr.markForCheck();
      });

    const isValidateAfterInit: boolean = this.activeRoute.snapshot.queryParams.validateAfterInit
      ? JSON.parse(this.activeRoute.snapshot.queryParams.validateAfterInit)
      : false;
    if (isValidateAfterInit) {
      this.fh.markAsTouched(this.itemForm);
    }

    if (!this.itemId) {
      this.isInit = true;
    }

    this.initializeCompanyFavoriteHtsNumbers();

    this.entrySummaryStateService.entrySummary$.pipe(takeUntil(this.isDestroy)).subscribe((entrySummary: EntrySummaryModel) => {
      this.entrySummary = entrySummary;
      this.isCanEdit = this.forceEditable || EntrySummaryActionsHelper.isCanEdit(entrySummary);
      this.isCustomBroker = this.entrySummaryStateService.isShowDocuments(entrySummary);
      this.isShowDocuments = this.isCustomBroker ? true : this.isShowDocuments;
      if (this.entrySummary && this.entrySummary.referenceNumber) {
        this.checkValidation();
      }
      if (entrySummary?.shipments?.length) {
        this.currentShipment = entrySummary.shipments.find((item: ShipmentModel) => item.id === this.shipmentId);
        this.currentInvoice = this.currentShipment?.invoices.find((item: InvoiceModel) => item.id === this.invoice.id);
        this.currentItem = this.currentInvoice?.lines.find((item: LineModel) => item.id === this.itemId);
        this.arrivalDate = entrySummary.arrivalDate;
        this.updateForm();
        this.updateCurrency();
        this.initializeCountryOfExport();
        this.initializeCountryOfOrigin();
        this.initState();
      }
      // see 5210
      // this.updateTotalESEnteredValue();

      let uploadDocumentsDescription: string,
        uploadDocumentsDescriptionMobile: string = null;
      switch (this.entrySummary?.transportationMode) {
        case enumTransportationMode.AIR:
          uploadDocumentsDescription = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_AIR';
          uploadDocumentsDescriptionMobile = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_MOBILE_AIR';
          break;
        case enumTransportationMode.OCEAN:
        case enumTransportationMode.RAIL:
        case enumTransportationMode.TRUCK:
          uploadDocumentsDescription = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_OCEAN';
          uploadDocumentsDescriptionMobile = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_MOBILE_OCEAN';
          break;
        default:
          break;
      }

      this.uploadDocumentsDescsription = uploadDocumentsDescription;
      this.uploadDocumentsDescsriptionMobile = uploadDocumentsDescriptionMobile;
    });

    this.entrySummaryStateService.entryDocuments$.pipe(takeUntil(this.isDestroy)).subscribe((documents: DocumentModel[]) => {
      this.attachedDocuments = documents;
      if (this.entrySummary && this.entrySummary.referenceNumber) {
        this.checkValidation();
      }
    });

    this.itemForm.get('countryOfExportId').clearValidators();

    this.favoriteHtsItemsSearchSubject
      .pipe(debounceTime(HTS_SUGGESTIONS_DEBOUNCE_MS), takeUntil(this.isDestroy))
      .subscribe((searchValue) => {
        if (!searchValue) return;

        const searchFilteredHtsList = this.getSearchFilteredHtsList(searchValue);

        return of(searchFilteredHtsList)
          .pipe(
            //Ensures invalid catalog items are filtered out from the results
            map((favoriteHtsItems) => {
              return favoriteHtsItems.filter((fhts) => fhts.isActive);
            }),
            takeUntil(this.isDestroy),
          )
          .subscribe((favoriteHtsItems) => {
            this.favoriteHtsNumberSearchedItems = favoriteHtsItems;
            this.cdr.markForCheck();
          });
      });

    // #13517: the same typed text also asks the backend for similar products (assistant-backed catalog items)
    this.favoriteHtsItemsSearchSubject
      .pipe(
        debounceTime(HTS_SUGGESTIONS_DEBOUNCE_MS),
        switchMap((searchValue: string) =>
          this.isBackendInsert
            ? this.htsLineInsertService.searchByText(searchValue, this.itemId || null)
            : of([] as SimilarHtsSuggestionModel[]),
        ),
        takeUntil(this.isDestroy),
      )
      .subscribe((suggestions: SimilarHtsSuggestionModel[]) => {
        this.favoriteHtsSuggestions = suggestions;
        this.cdr.markForCheck();
      });
  }

  public get showState(): boolean {
    const id: number = this.itemForm.get('countryOfOriginId')?.value;
    return id === CANADA_COUNTRY_ID || id === MEXICO_COUNTRY_ID || id === USA_COUNTRY_ID;
  }

  public get invoiceNumber(): string {
    return (this.currentInvoice && this.currentInvoice.invoiceNumber) || '';
  }

  public get isRequiredHtsNumber(): boolean {
    return this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL;
  }

  public get isShowFindInCatalog(): boolean {
    return !!this.companyFavoriteHtsNumbers.length && !this.isReadMode;
  }

  public get isShowClearForm(): boolean {
    return !this.isReadMode;
  }

  /**
   * Read mode flag for a missing description: a disabled control carries no validation state (no `.invalid` class, no
   * control-errors message), so the wrapper is told explicitly — same look as the live validation.
   */
  public get isShowDescriptionMissingReadonly(): boolean {
    return (
      this.isReadMode &&
      this.isAssistantDescriptionRequested &&
      !!this.itemForm &&
      !HtsAssistantHelper.normalizeDescription(this.itemForm.get('description').value)
    );
  }

  public get descriptionGroupClass(): string {
    return this.isShowDescriptionMissingReadonly ? 'invalid' : '';
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public toggleSpecialHts(): void {
    this.addSpecialChapterHTSCode = !this.addSpecialChapterHTSCode;
    if (!this.addSpecialChapterHTSCode) {
      this.itemForm.get('specialChapterHtsNumber').patchValue(null);
      this.itemForm.get('htsNumber').updateValueAndValidity();
    }
  }

  public submit(needValidate: boolean = true): void {
    if (!this.isCanEdit) {
      return;
    }
    if (needValidate && this.itemForm.invalid) {
      const invalidFields: any = this.fh.validateAllFields(this.itemForm);

      if (invalidFields.number && this.itemForm.get('specialChapterHts').get('number').invalid) {
        delete invalidFields.number;
        invalidFields.specialChapterHtsCode = 'specialChapterHtsCode';
      }

      if (Object.keys(invalidFields).length) {
        this.openModal(invalidFields);
        this.cdr.markForCheck();
        return;
      }
    }

    this.lineDataSaved.emit(this.buildLinePayload());
    // #12298: persistence is delegated to the parent (async), so do NOT markAsPristine here — that would clear dirty
    // before the save is confirmed and let a racing background reload clobber the input if the save fails. This editor
    // is short-lived (closes / navigates on save), so the form simply stays dirty and the guard keeps protecting it.
  }

  public searchUnits(): Observable<UnitModel[]> {
    return this.codeRepository.searchLineUnits.bind(this.codeRepository);
  }

  public onFavoriteHtsItemsSearch() {
    //#11822 - skip search if no catalog items are saved for company
    if (!this.companyFavoriteHtsNumbers.length) {
      return;
    }

    const favoriteHtsItemsSearchText = this.itemForm.get('description').value;
    this.favoriteHtsItemsSearchSubject.next(favoriteHtsItemsSearchText);
  }

  /** The assistant applied a code to this line (already persisted): mirror it in the form, other edits untouched. */
  public applyHts(data: { hts: string; description: string | null }): void {
    this.setHTS({ hts: data.hts, description: data.description || this.itemForm.get('description').value });
    this.cdr.markForCheck();
  }

  /** Embedded hosts (the assistant's line panel): drop the unsaved edits and show the stored line again. */
  public revert(): void {
    this.itemForm.markAsPristine();
    this.updateForm();
    this.cdr.markForCheck();
  }

  /**
   * #13384 F15: the assistant classifies the SAVED item description, so there is nothing to start without one. Flags the
   * field (its own hint wins over the generic "required" one), focuses it and returns false; typing clears the flag.
   */
  public requireDescriptionForAssistant(): boolean {
    const isPresent: boolean = HtsAssistantDescriptionGuard.require(this.itemForm.get('description'), this.elementRef.nativeElement);
    this.isAssistantDescriptionRequested = !isPresent;
    this.cdr.markForCheck();
    return isPresent;
  }

  /** The legacy pick (untagged companies): the item's title (else description), cut to the field limit, and the code go into the form. */
  public onHtsInserted(favoriteHtsNumber: FavoriteHtsNumberModel): void {
    const description: string = favoriteHtsNumber.title || favoriteHtsNumber.description || '';
    this.setHTS({
      hts: favoriteHtsNumber.htsNumber,
      description: UtilsHelper.limitLength(description, LINE_ITEM_DESCRIPTION_MAX_LENGTH, false),
    });
    this.clearSearchedFavoriteHtsItems();
    this.cdr.markForCheck();
  }

  public clearSearchedFavoriteHtsItems() {
    this.favoriteHtsNumberSearchedItems = [];
    this.favoriteHtsSuggestions = [];
    // an empty text supersedes a pending debounce and the similar-products request in flight: nothing reopens the dropdown
    this.favoriteHtsItemsSearchSubject.next('');
  }

  /** The fast-search dropdown: exact catalog matches first, then the similar products (#13517). */
  public get isShowFastSearch(): boolean {
    return this.favoriteHtsNumberSearchedItems.length > 0 || this.favoriteHtsSuggestions.length > 0;
  }

  /** Tagged companies editing their own entry insert catalog codes through the backend (code only); everyone else fills the form. */
  public get isBackendInsert(): boolean {
    const scope: enumHtsChatScope = this.htsChatScope || enumHtsChatScope.Importer;
    return this.htsLineInsertService.isEnabled && this.isCanEdit && scope === enumHtsChatScope.Importer;
  }

  /**
   * A catalog pick from the dropdown or the sidebar (#13411). Tagged companies: the line is saved first and the backend
   * writes the code — the description stays the user's own (an empty one takes the item's title so the line can be saved).
   */
  public onCatalogPick(favoriteHtsNumber: FavoriteHtsNumberModel): void {
    if (!this.isBackendInsert) {
      this.onHtsInserted(favoriteHtsNumber);
      return;
    }
    this.clearSearchedFavoriteHtsItems();
    this.fillEmptyDescription(favoriteHtsNumber.title || favoriteHtsNumber.description);
    this.insertThroughBackend((lineId: number) => this.htsLineInsertService.insertCatalogItem(lineId, favoriteHtsNumber));
  }

  /** A similar-product suggestion (#13517): confirmed through the backend, the line comes back "matched by similarity". */
  public onSuggestionPick(suggestion: SimilarHtsSuggestionModel): void {
    this.clearSearchedFavoriteHtsItems();
    if (!this.isBackendInsert) {
      return;
    }
    this.fillEmptyDescription(suggestion.title || suggestion.matchedText);
    this.insertThroughBackend((lineId: number) => this.htsLineInsertService.confirmSuggestion(lineId, suggestion));
  }

  public getHtsUnits(data: HtsNumberModel[]): void {
    if (data && (isNaN(this.currentItem?.quantity1) || this.currentItem?.quantity1 === null)) {
      const prevValues: any[] = this.itemForm.get('units').value;
      const items: UntypedFormArray = this.itemForm.get('units') as UntypedFormArray;
      const hts: HtsNumberModel = data.length === 1 ? data[0] : null;
      if (items.length) {
        while (items.length !== 0) {
          items.removeAt(0);
        }
      }
      if (hts) {
        this.setLastLeadHtsUnits(hts);
        if (hts.unit1) {
          items.push(this.createUnit(1, hts.unit1, this.getQuantity(prevValues, 1)));
        }
        if (hts.unit2) {
          items.push(this.createUnit(2, hts.unit2, this.getQuantity(prevValues, 2)));
        }
        if (hts.unit3) {
          items.push(this.createUnit(3, hts.unit3, this.getQuantity(prevValues, 3)));
        }
        this.unitsToMachForSpecialHts = [hts.unit1, hts.unit2, hts.unit3].filter((unit: string) => !!unit);
      }
      this.cdr.markForCheck();
    }
  }

  public removeUnit(index: number): void {
    const items: UntypedFormArray = this.itemForm.get('units') as UntypedFormArray;
    const units: Array<{ quantity: number; unitOfMeasure: string }> = items.value
      .filter((unit: any) => !unit.hasOwnProperty('quantity' + (index + 1)))
      .map((unit: any) => ({
        quantity: unit.quantity1 || unit.quantity2 || unit.quantity3 || null,
        unitOfMeasure: unit.unitOfMeasure1 || unit.unitOfMeasure2 || unit.unitOfMeasure3 || null,
      }));
    if (items.controls.length > 0) {
      while (items.length !== 0) {
        items.removeAt(0);
      }
    }

    units.forEach((item: { quantity: number; unitOfMeasure: string }, unitIndex: number) => {
      items.push(this.createUnit(unitIndex + 1, item.unitOfMeasure, item.quantity));
    });
  }

  public back(): void {
    this.location.back();
  }

  public get formUnits(): UntypedFormArray {
    return <UntypedFormArray>this.itemForm.get('units');
  }

  public onDocumentStateUpdate(data: IEsDocumentStateUpdate): void {
    this.entrySummaryStateService.updateDocumentState(data.documentId, data.state);
  }

  public setLastLeadHtsUnits(units: any): void {
    this.leadHtsUnits = {
      leadUnit1: units.unit1,
      leadUnit2: units.unit2,
      leadUnit3: units.unit3,
    };
  }

  public getCountries(): Observable<CountryModel[]> {
    return this.getCountriesCallApi.bind(this);
  }

  public getCountriesCallApi(code: string): Observable<CountryModel[]> {
    const call: Observable<CountryModel[]> = this.codeRepository.searchCountry(code);
    call.pipe(take(1)).subscribe((data: CountryModel[]) => (this.lastSearchedCountries = data));

    return call;
  }

  public getStates(): Observable<StateModel[]> {
    return this.codeRepository.searchState.bind(this.codeRepository, this.itemForm.get('countryOfOrigin')?.value?.code);
  }

  public clearForm(): void {
    this.itemForm.reset({
      id: this.itemForm.get('id')?.value,
      hasNaftaNetCostIndicator: this.itemForm.get('hasNaftaNetCostIndicator')?.value,
    });
  }

  public findInCatalog() {
    if (this.isHideFindHts) {
      this.findInCatalogRequested.emit();
      return;
    }
    this.appService.isXlg$.next(true);

    const componentSidebarRef: ComponentRef<BinaCatalogSearchSidebarComponent> = this.appService.openSidebarAsWindow(
      BinaCatalogSearchSidebarComponent,
      {
        sidebarHeaderText: '',
        isMd: true,
        customZIndex: '1001',
      },
    );

    componentSidebarRef.instance.company = this.companiesService.getCompany();
    componentSidebarRef.instance.initialSearch = this.isBackendInsert ? this.itemForm.get('description').value : null;

    // the sidebar pick follows the dropdown pick: title as the description (#13545), backend insert for tagged companies
    componentSidebarRef.instance.htsInsert.pipe(takeUntil(this.isDestroy)).subscribe((favoriteHts: FavoriteHtsNumberModel) => {
      this.closeCatalogSidebar();
      this.onCatalogPick(favoriteHts);
    });

    componentSidebarRef.instance.suggestionInsert.pipe(takeUntil(this.isDestroy)).subscribe((suggestion: SimilarHtsSuggestionModel) => {
      this.closeCatalogSidebar();
      this.onSuggestionPick(suggestion);
    });

    componentSidebarRef.instance.close.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
  }

  public get isHtsAssistantEnabled(): boolean {
    return this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant);
  }

  public findHTS(): void {
    if (this.isHtsAssistantEnabled) {
      if (this.requireDescriptionForAssistant()) {
        this.openHtsAssistant();
      }
      return;
    }

    this.appService.isXlg$.next(true);

    const componentSidebarRef: ComponentRef<BinaSidebarComponent> = this.appService.openSidebarAsWindow(BinaSidebarComponent, {
      sidebarHeaderText: '',
      isMd: true,
    });

    componentSidebarRef.instance.importerId = this.entrySummary?.importerId;
    componentSidebarRef.instance.manufacturerId = this.currentInvoice?.manufacturerId;
    componentSidebarRef.instance.existingDescription = this.itemForm.get('description').value;
    componentSidebarRef.instance.existingHTS = this.itemForm.get('htsNumber').value;
    componentSidebarRef.instance.htsFound.pipe(takeUntil(this.isDestroy)).subscribe((data: { hts: string; description: string }) => {
      this.setHTS(data);
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
    componentSidebarRef.instance.close.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
  }

  /** Only an EMPTY description is filled from the catalog, so a line can be saved; the user's own text is never replaced. */
  private fillEmptyDescription(text: string | null | undefined): void {
    const current: string = this.itemForm.get('description').value;
    if (HtsAssistantHelper.normalizeDescription(current) || !text) {
      return;
    }
    this.itemForm.get('description').setValue(UtilsHelper.limitLength(text, LINE_ITEM_DESCRIPTION_MAX_LENGTH, false));
    this.itemForm.get('description').markAsDirty();
    this.itemForm.markAsDirty();
  }

  /** Save the draft (POST / PUT / nothing), let the backend write the code, mirror only the code in the form. */
  private insertThroughBackend(request: (lineId: number) => Observable<LineModel>): void {
    if (this.isPreSavingForAssistant) {
      return;
    }
    this.isPreSavingForAssistant = true;
    this.saveLineForAssistant()
      .pipe(
        switchMap(request),
        take(1),
        takeUntil(this.isDestroy),
        finalize(() => {
          this.isPreSavingForAssistant = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(
        (line: LineModel) => {
          this.itemForm.get('htsNumber').setValue(line.htsNumber);
          this.lineInserted.emit(line);
        },
        // the service already raised the alert; the draft stays as it is
        () => this.lineInsertFailed.emit(),
      );
  }

  private closeCatalogSidebar(): void {
    this.appService.closeSidebarAsWindow();
    this.appService.isXlg$.next(false);
  }

  private initializeCompanyFavoriteHtsNumbers() {
    this.binaRepository
      .searchFavoriteHtsNumbers({ companyId: this.company?.id })
      .pipe(takeUntil(this.isDestroy))
      .subscribe((result) => {
        const { items } = result;
        this.companyFavoriteHtsNumbers = items;
        this.cdr.detectChanges();
      });
  }

  private getSearchFilteredHtsList(searchValue: string): FavoriteHtsNumberModel[] {
    return this.companyFavoriteHtsNumbers.filter((fhts) => {
      const lowerCaseSearchValue = searchValue.toLocaleLowerCase();

      const isMatchWithHtsNumber = fhts?.htsNumber.toLowerCase().includes(lowerCaseSearchValue);
      const isMatchWithSearchQuery = fhts?.searchQuery.toLowerCase().includes(lowerCaseSearchValue);
      const isMatchWithTitle = fhts?.title.toLowerCase().includes(lowerCaseSearchValue);
      const isMatchWithDescription = fhts?.description.toLowerCase().includes(lowerCaseSearchValue);

      return isMatchWithHtsNumber || isMatchWithSearchQuery || isMatchWithTitle || isMatchWithDescription;
    });
  }

  private initializeCountryOfOrigin(): void {
    if (!this.itemForm.get('countryOfOriginId').value) {
      return;
    }

    if (this.itemForm.get('countryOfOrigin')?.value?.code) {
      this.codeRepository
        .getCountry(this.itemForm.get('countryOfOrigin')?.value?.code)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((country: CountryModel) => {
          this.initCountryOfOrigin = [country];
          this.cdr.markForCheck();
        });
    }
  }

  private initializeCountryOfExport(): void {
    if (!this.itemForm.get('countryOfExportId').value) {
      return;
    }

    if (this.itemForm.get('countryOfExport')?.value?.code) {
      this.codeRepository
        .getCountry(this.itemForm.get('countryOfExport')?.value?.code)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((country: CountryModel) => {
          this.initCountryOfExport = [country];
          this.cdr.markForCheck();
        });
    }
  }

  private initState(): void {
    if (!this.itemForm.get('stateOfOrigin').value) {
      return;
    }
    this.codeRepository
      .getState(this.itemForm.get('stateOfOrigin').value)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((state: StateModel) => {
        this.initStateOfOrigin = [state];
      });
  }

  private checkValidation(): void {
    this.entrySummaryStateService
      .getESValidationErrorData(this.entrySummary, this.attachedDocuments)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((data: IEsValidationData) => {
        this.validationMessages = data.messages;
      });
  }

  private getQuantity(prevValues: any[], index: number): number {
    return prevValues && prevValues.length > index - 1 && prevValues[index - 1]['quantity' + index]
      ? prevValues[index - 1]['quantity' + index]
      : null;
  }

  private createUnit(index: number, unit: string, quantity: string | number = ''): UntypedFormGroup {
    const form: UntypedFormGroup = new UntypedFormGroup({
      ['quantity' + index]: new UntypedFormControl(quantity, this.isRequiredHtsNumber ? Validators.required : []),
      ['unitOfMeasure' + index]: new UntypedFormControl(unit, this.isRequiredHtsNumber ? Validators.required : []),
    });

    if (this.itemForm.disabled) {
      form.disable();
    } else {
      form.enable();
    }
    return form;
  }

  /** Read mode = every control disabled; `updateForm` re-applies it on every reload. */
  private applyReadMode(): void {
    if (!this.itemForm) {
      return;
    }
    if (this.isCanEdit && !this.isReadMode) {
      this.itemForm.enable();
    } else {
      this.itemForm.disable();
    }
  }

  private openModal(data): void {
    this.missingFieldModal.openCustomModal(data);
  }

  private updateForm(): void {
    if (!this.currentItem) {
      return;
    }
    // #12298: don't re-patch the form from a background reload while the user is mid-edit — it would clobber unsaved input.
    if (this.itemForm.dirty) {
      return;
    }
    const dataToUpdate = {
      id: this.itemId || null,
      countryOfOrigin: this.currentItem.countryOfOrigin,
      countryOfOriginId: this.currentItem.countryOfOrigin?.id,
      htsNumber: this.currentItem.htsNumber,
      stateOfOrigin: this.currentItem.stateOfOrigin,
      description: this.currentItem.description,
      grossWeight: !!this.currentItem.grossWeight ? this.currentItem.grossWeight : '',
      value: !!this.currentItem.value ? this.currentItem.value : '',
      specialChapterHtsNumber: this.currentItem.specialChapterHtsNumber,
      hasNaftaNetCostIndicator: this.currentItem.hasNaftaNetCostIndicator,
      countryOfExport: this.invoice?.countryOfExport,
      countryOfExportId: this.invoice?.countryOfExportId,
    };

    this.itemForm.patchValue(dataToUpdate, { emitEvent: false });

    const units: UntypedFormArray = this.itemForm.get('units') as UntypedFormArray;
    if (units.length) {
      while (units.length !== 0) {
        units.removeAt(0);
      }
    }
    if (this.currentItem.quantity1 || this.currentItem.unitOfMeasure1) {
      units.push(this.createUnit(1, this.currentItem.unitOfMeasure1, !!this.currentItem.quantity1 ? this.currentItem.quantity1 : ''));
    }
    if (this.currentItem.quantity2 || this.currentItem.unitOfMeasure2) {
      units.push(this.createUnit(2, this.currentItem.unitOfMeasure2, !!this.currentItem.quantity2 ? this.currentItem.quantity2 : ''));
    }
    if (this.currentItem.quantity3 || this.currentItem.unitOfMeasure3) {
      units.push(this.createUnit(3, this.currentItem.unitOfMeasure3, !!this.currentItem.quantity3 ? this.currentItem.quantity3 : ''));
    }
    if (this.currentItem.specialChapterHtsNumber) {
      this.addSpecialChapterHTSCode = true;
    }
    this.applyReadMode();

    this.isInit = true;
  }

  private updateCurrency(): void {
    if (!this.currentShipment || !this.currentInvoice) {
      this.invoiceCurrencyCode = DEFAULT_CURRENCY_CODE;
      return;
    }
    this.invoiceCurrencyCode = this.currentInvoice.currency || DEFAULT_CURRENCY_CODE;
    const exportDate: string = this.currentShipment.exportDate || DateHelper.now;
    if (this.invoiceCurrencyCode !== DEFAULT_CURRENCY_CODE && this.invoiceCurrencyCode) {
      this.currenciesRepository
        .getRate(this.invoiceCurrencyCode, exportDate)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((currencyRate: CurrencyRateModel) => {
          this.invoiceCurrencyRate = currencyRate ? currencyRate.rate : NaN;
          // TODO: check setCurrencyRate using, not direct logic
          this.cdr.detectChanges();
          // see 5210
          // this.updateTotalESEnteredValue();
        });
    } else {
      this.invoiceCurrencyRate = 1;
    }
  }

  private createShouldNotMatchHTSConfig(): any {
    return {
      htsNumber: {
        relatedControlName: 'specialChapterHtsNumber',
        errorMessage: 'shouldNotMatchSpecialChapterHts',
        relatedControlErrorMessage: 'shouldNotMatchHts',
      },
      specialChapterHtsNumber: {
        relatedControlName: 'htsNumber',
        errorMessage: 'shouldNotMatchHts',
        relatedControlErrorMessage: 'shouldNotMatchSpecialChapterHts',
      },
    };
  }

  /** What Save sends; `id` stays explicit (null for a new line) — the mobile invoice page seeds its own id otherwise. */
  private buildLinePayload(): LineModel {
    const { htsNumber, countryOfOrigin, units, specialChapterHtsNumber } = this.itemForm.value;
    const dataUnits = units.reduce((store, item) => {
      const keys = Object.keys(item);
      const updatedItem = {};
      keys.forEach((dataItem: string) => {
        if (!dataItem.includes('unitOfMeasure')) {
          updatedItem[dataItem] = !isNaN(Number(item[dataItem])) ? Number(item[dataItem]) : item[dataItem];
        } else {
          updatedItem[dataItem] = item[dataItem];
        }
      });
      store = { ...store, ...updatedItem };
      return store;
    }, {});
    return {
      ...this.itemForm.value,
      ...dataUnits,
      htsNumber,
      countryOfOrigin,
      specialChapterHtsNumber: this.addSpecialChapterHTSCode ? specialChapterHtsNumber : null,
      id: this.itemId || null,
      grossWeight: Number(this.itemForm.value.grossWeight),
      value: Number(this.itemForm.value.value),
    };
  }

  /**
   * #13383 F6: "Find HTS" persists the draft first so the assistant talks to a saved line — POST for a new line (whatever
   * the form holds, validation skipped), PUT for a changed one, nothing for an untouched one. Pristine is set only after the
   * entry reload so `updateForm()` cannot overwrite the draft meanwhile.
   */
  private saveLineForAssistant(): Observable<number> {
    if (this.itemId && !this.itemForm.dirty) {
      return of(this.itemId);
    }
    const payload: LineModel = this.buildLinePayload();
    const save$: Observable<LineModel> = this.itemId
      ? this.entrySummaryStateService.updateLineItem(payload, this.itemId)
      : this.entrySummaryStateService.createLineItem(payload, this.invoice.id);
    return save$.pipe(
      tap((line: LineModel) => {
        if (!this.itemId) {
          this.itemId = line.id;
          this.lineCreated.emit(line.id);
        }
      }),
      switchMap((line: LineModel) =>
        this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber).pipe(
          tap((entry: EntrySummaryModel) => this.entrySummaryStateService.updateCurrentEntrySummary(entry)),
          map(() => line.id),
          // the line IS saved: a failed refresh must not hide the drawer or leave the id behind (next reload heals the lists)
          catchError(() => of(line.id)),
        ),
      ),
      tap(() => {
        this.itemForm.markAsPristine();
        this.cdr.markForCheck();
      }),
    );
  }

  /** #13383: the classification assistant drawer replaces the BINA classifier for tagged companies. */
  private openHtsAssistant(): void {
    if (this.isPreSavingForAssistant) {
      return;
    }
    const scope: enumHtsChatScope = this.htsChatScope || enumHtsChatScope.Importer;
    const mode: enumHtsAssistantMode =
      this.isCanEdit && scope === enumHtsChatScope.Importer ? enumHtsAssistantMode.Live : enumHtsAssistantMode.Readonly;
    const lineId$: Observable<number | null> = mode === enumHtsAssistantMode.Live ? this.saveLineForAssistant() : of(this.itemId || null);
    this.isPreSavingForAssistant = true;
    lineId$.pipe(take(1), takeUntil(this.isDestroy)).subscribe(
      (lineId: number | null) => {
        this.isPreSavingForAssistant = false;
        this.launchHtsAssistant(mode, scope, lineId);
      },
      () => (this.isPreSavingForAssistant = false), // the global alert already told the user; the drawer stays closed
    );
  }

  private launchHtsAssistant(mode: enumHtsAssistantMode, scope: enumHtsChatScope, lineId: number | null): void {
    this.lineModifiedAtDrawerOpen = this.currentItem ? this.currentItem.lastModified : null;
    const drawerRef: ComponentRef<HtsAssistantDrawerComponent> = this.htsAssistantLauncherService.open({
      mode,
      scope,
      lineId,
      invoiceId: this.invoice ? this.invoice.id : null,
      companyId: this.company ? this.company.id : null,
      lineDescription: this.itemForm.get('description').value,
      currentHts: this.itemForm.get('htsNumber').value,
      hostDestroy$: this.isDestroy,
      draftDescription$: this.itemForm.get('description').valueChanges,
      // the panel edits the stored line; an add-mode form has no stored line to follow (its edits would be PUT over)
      isLinePanel: !!this.itemId,
    });

    drawerRef.instance.htsFound.pipe(takeUntil(this.isDestroy)).subscribe((data: IHtsAssistantHtsFound) => {
      // only the code comes from the assistant: the description stays the user's own (#13384 F15)
      this.setHTS({ hts: data.hts, description: data.description || this.itemForm.get('description').value });
      this.cdr.markForCheck();
    });
    // closed by the user, after an Insert (#13529) or with the host: the line panel may have saved the line meanwhile
    drawerRef.onDestroy(() => this.onAssistantClosed());
  }

  /**
   * The drawer is gone (a microtask later): its line panel may have saved the line, so the form follows the store — but
   * only if the store line really changed: after a failed entry re-read the form (with the code the assistant put there)
   * is the better copy.
   */
  private onAssistantClosed(): void {
    Promise.resolve().then(() => {
      if (this.isDestroyed) {
        return;
      }
      const isStoreLineChanged: boolean = !!this.currentItem && this.currentItem.lastModified !== this.lineModifiedAtDrawerOpen;
      if (isStoreLineChanged) {
        this.itemForm.markAsPristine();
        this.updateForm();
      }
      this.cdr.markForCheck();
    });
  }

  private setHTS(data: { hts: string; description: string }): void {
    if (!data) {
      return;
    }
    this.itemForm.get('htsNumber').setValue(data.hts);
    this.itemForm.get('description').setValue(data.description);
  }
}
